import { BookOpen, Download, Share2, X } from "lucide-react";
import { useEffect, useState } from "react";

export function IOSInstallGuide() {
  const [isOpen, setIsOpen] = useState(false);
  const [isIOSUser, setIsIOSUser] = useState(false);

  useEffect(() => {
    // Detect iOS
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
    const isInStandaloneMode = window.navigator.standalone === true;

    setIsIOSUser(isIOS);

    // Show guide if iOS and NOT in standalone mode (not already installed)
    if (isIOS && !isInStandaloneMode) {
      // Check if user has dismissed this before (localStorage)
      const dismissed = localStorage.getItem("ios_pwa_dismissed");
      if (!dismissed) {
        // Show after 2 seconds
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    // Remember dismissal for 7 days
    localStorage.setItem("ios_pwa_dismissed", "true");
    setTimeout(
      () => {
        localStorage.removeItem("ios_pwa_dismissed");
      },
      7 * 24 * 60 * 60 * 1000,
    ); // 7 days
  };

  if (!isIOSUser || !isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end z-50 p-4">
      {/* Modal - scrollable and centered with padding */}
      <div className="w-full max-w-md bg-brand-surface border border-brand-border rounded-3xl shadow-2xl animate-in fade-in slide-in-from-bottom-6 duration-300 max-h-[85vh] overflow-y-auto">
        {/* Header - sticky */}
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-brand-border bg-brand-surface rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="bg-brand-green/20 rounded-lg p-2.5">
              <Download className="w-5 h-5 text-brand-green" />
            </div>
            <h2 className="font-semibold text-lg text-brand-text">
              Add to Home Screen
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-brand-dim hover:text-brand-text transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="p-6 space-y-6">
          {/* Info Section */}
          <div>
            <p className="text-sm text-brand-text mb-4">
              Save diet-reg to your home screen for instant access, just like a
              native app!
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-brand-green/20 border border-brand-green/40">
                  <span className="text-sm font-semibold text-brand-green">
                    1
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-brand-text mb-1">
                  Tap the Share button
                </h4>
                <p className="text-xs text-brand-dim">
                  Look for the Share icon (arrow pointing up) at the bottom of
                  Safari
                </p>
                <div className="mt-2 bg-brand-bg rounded-lg p-2 flex items-center justify-center border border-brand-border">
                  <Share2 className="w-4 h-4 text-brand-green" />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-brand-green/20 border border-brand-green/40">
                  <span className="text-sm font-semibold text-brand-green">
                    2
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-brand-text mb-1">
                  Select &quot;Add to Home Screen&quot;
                </h4>
                <p className="text-xs text-brand-dim">
                  Scroll down in the Share menu to find this option
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-brand-green/20 border border-brand-green/40">
                  <span className="text-sm font-semibold text-brand-green">
                    3
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-brand-text mb-1">
                  Tap &quot;Add&quot;
                </h4>
                <p className="text-xs text-brand-dim">
                  Confirm and the app will be added to your home screen
                </p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-gradient-to-br from-brand-green/10 to-brand-green/5 rounded-xl p-4 border border-brand-green/30">
            <div className="flex items-start gap-3">
              <BookOpen className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-brand-text mb-1">
                  Why add to home screen?
                </p>
                <ul className="text-xs text-brand-dim space-y-1">
                  <li>✓ Launch instantly like a native app</li>
                  <li>✓ Works offline for viewing past logs</li>
                  <li>✓ No browser chrome, full screen experience</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Safari Tip */}
          <div className="bg-brand-amber/10 rounded-lg p-3 border border-brand-amber/30">
            <p className="text-xs text-brand-amber text-center">
              💡 <strong>Tip:</strong> Safari provides the best PWA experience
              on iOS
            </p>
          </div>

          {/* Action */}
          <button
            onClick={handleClose}
            className="w-full bg-brand-green hover:bg-green-500 text-brand-bg font-semibold py-3 rounded-lg transition-colors"
          >
            Got It!
          </button>
        </div>
      </div>
    </div>
  );
}
